import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '../../../theme/useTheme';
import FontFamily from '../../../constants/fonts';

export type TagVariant = 'blue' | 'green' | 'amber' | 'red' | 'gray' | 'teal' | 'purple' | 'orange';

const VARIANT_STYLES_LIGHT: Record<TagVariant, { bg: string; text: string }> = {
  blue:   { bg: '#DAEEFA', text: '#01346F' },
  green:  { bg: '#CAF3C6', text: '#0C420B' },
  amber:  { bg: '#FEF1B9', text: '#995211' },
  red:    { bg: '#FFE5DE', text: '#AB2B09' },
  gray:   { bg: '#E6E6E6', text: '#4B4B4B' },
  teal:   { bg: '#AAD2E2', text: '#1E5A7A' },
  purple: { bg: '#E1E3FD', text: '#31217D' },
  orange: { bg: '#FCECDE', text: '#4D2E0C' },
};

// Dark mode: semi-transparent tinted bg, bright readable text
const VARIANT_STYLES_DARK: Record<TagVariant, { bg: string; text: string }> = {
  blue:   { bg: 'rgba(61,139,255,0.22)',  text: '#99CFFF' },
  green:  { bg: 'rgba(56,182,56,0.22)',   text: '#8FEBA8' },
  amber:  { bg: 'rgba(217,150,0,0.22)',   text: '#FFD166' },
  red:    { bg: 'rgba(220,53,53,0.22)',   text: '#FFB09A' },
  gray:   { bg: 'rgba(176,176,176,0.18)', text: '#D4D4D4' },
  teal:   { bg: 'rgba(32,160,192,0.22)',  text: '#7DE8FF' },
  purple: { bg: 'rgba(128,90,213,0.22)',  text: '#CEC0FF' },
  orange: { bg: 'rgba(220,120,0,0.22)',   text: '#FFBA7A' },
};

interface AdminTagProps {
  label: string;
  variant?: TagVariant;
  /** Override background color directly */
  bgColor?: string;
  /** Override text color directly */
  textColor?: string;
  small?: boolean;
}

const AdminTag: React.FC<AdminTagProps> = ({
  label,
  variant = 'blue',
  bgColor,
  textColor,
  small = false,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const palette = isDark ? VARIANT_STYLES_DARK[variant] : VARIANT_STYLES_LIGHT[variant];
  const bg = bgColor ?? palette.bg;
  const text = textColor ?? palette.text;

  return (
    <View style={[styles.tag, { backgroundColor: bg }, small && styles.small]}>
      <Text style={[styles.label, { color: text }, small && styles.labelSmall]}>{label}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  tag: {
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    fontSize: 12,
    fontWeight: '500',
    fontFamily: FontFamily.medium,
  },
  small: {
    height: 20,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  labelSmall: {
    fontSize: 11,
  },
});

export default AdminTag;
